import { COLOR_OPTIONS } from '@protector/styles';
import { Tooltip } from 'antd';
import classNames from 'classnames';
import type React from 'react';
import { memo, useCallback, useRef, useState } from 'react';
import { parseMaxlengthString } from './typo.helpers';
import type { FontFamily, STTypoProps } from './typo.models';
import { TypeOptions } from './typo.models';
import './typo.styles.css';

const STTypo = <T extends FontFamily>({
  type = TypeOptions.P1,
  boldness = false,
  lightWeight = false,
  italic = false,
  color = 'inherit',
  nowrap = false,
  uppercase = false,
  capitalize = false,
  lowercase = false,
  tagged = false,
  forcewrap = false,
  borderedFont = false,
  shouldHideTooltip = false,
  tooltipWhenTruncated = false,
  align = 'inherit',
  maxlength = 0,
  children,
  lineHeight,
  fontFamily,
  fontWeight,
  horizontalPadding = false,
  customClassName,
  ...props
}: React.PropsWithChildren<STTypoProps<T>>): JSX.Element => {
  const textIsBig = !!(maxlength && typeof children === 'string' && children.length > maxlength + 3);
  const resultNode = parseMaxlengthString(children, maxlength, textIsBig);

  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncatedTooltipOpen, setIsTruncatedTooltipOpen] = useState(false);

  const handleTruncatedTooltipOpenChange = useCallback((shouldOpen: boolean) => {
    const element = textRef.current;
    setIsTruncatedTooltipOpen(shouldOpen && !!element && element.scrollWidth > element.clientWidth);
  }, []);

  const isTruncated = tooltipWhenTruncated && !textIsBig;

  const typoClasses = classNames(
    {
      'st-typo': true,
      'st-typo--uppercase': uppercase,
      'st-typo--capitalize': capitalize,
      'st-typo--lowercase': lowercase,
      'st-typo--tagged': tagged,
      'st-typo--boldness': boldness,
      'st-typo--light-weight': lightWeight,
      'st-typo--italic': italic,
      'st-typo--primary': color === COLOR_OPTIONS.PRIMARY.toString(),
      'st-typo--secondary': color === COLOR_OPTIONS.SECONDARY.toString(),
      'st-typo--nowrap': nowrap || isTruncated,
      'st-typo--truncated': isTruncated,
      'st-typo--forcewrap': forcewrap,
      'st-typo--bordered-font': borderedFont,
      'st-typo--horizontal-padding': horizontalPadding,
      [type]: true
    },
    customClassName
  );

  const fontFamilyWithFallback = fontFamily;
  const style: React.HTMLAttributes<HTMLSpanElement>['style'] = {
    color: color as string,
    textAlign: align,
    lineHeight,
    fontFamily: fontFamilyWithFallback,
    fontWeight,
    overflowWrap: forcewrap ? 'break-word' : undefined
  };

  const typoNode = (
    <span ref={textRef} className={typoClasses} style={style} {...props}>
      {textIsBig && !shouldHideTooltip ? <Tooltip title={children}>{resultNode}</Tooltip> : resultNode}
    </span>
  );

  if (!isTruncated || shouldHideTooltip) {
    return typoNode;
  }

  return (
    <Tooltip title={children} open={isTruncatedTooltipOpen} onOpenChange={handleTruncatedTooltipOpenChange}>
      {typoNode}
    </Tooltip>
  );
};

const STTypoMemoized = memo(STTypo);
export default STTypoMemoized;
